import Carousel from '../legacy/components/carousel.js';
import { createDebugger } from '../utils/debug.js';
import '../utils/resize-event.js';

const debug = createDebugger('text-content-slider', false);

/**
 * Text Content Slider Component
 * Initializes Swiper carousel for text-with-content slides
 */
class TextContentSlider {
    constructor(container) {
        debug.log('Constructor called', { container });
        this.container = container;
        this.carousel = null;
        this.swiperInstance = null;

        // Cache DOM elements
        this.swiperContainer = null;
        this.pagination = null;
        this.paginationParent = null;

        // Bind resize handler
        this._handleResize = this._handleResize.bind(this);

        try {
            this._cacheElements();
            this._initCarousel();
            this._setupResizeListener();
            debug.log('Carousel initialized successfully');
        } catch (error) {
            debug.error('Error initializing carousel:', error);
        }
    }

    /**
     * Cache frequently used DOM elements
     */
    _cacheElements() {
        debug.log('_cacheElements called');

        // Cache swiper container
        this.swiperContainer = this.container.querySelector('.swiper');
        debug.log('Swiper container cached:', this.swiperContainer);

        // Cache custom pagination buttons
        this.paginationButtons = [];
    }

    _initCarousel() {
        debug.log('_initCarousel called');

        if (!this.swiperContainer) {
            debug.error('No .swiper element found inside container');
            return;
        }

        // Check for slides
        const slides = this.swiperContainer.querySelectorAll('.swiper-slide');
        debug.log(`Found ${slides.length} slides`);

        if (slides.length === 0) {
            debug.warn('No slides found, carousel may not work properly');
            return;
        }

        // Setup pagination event listeners
        this._setupPaginationListeners();

        const carouselOptions = {
            slidesPerView: 1,
            spaceBetween: 0,
            grabCursor: true,
            loop: false,
            speed: 600,
            autoHeight: true,
            width: window.innerWidth, // Set to viewport width
            watchOverflow: true,
            centeredSlides: true,
            pagination: false, // Disable Swiper's built-in pagination
            breakpoints: {
                768: {
                    autoHeight: false,
                    width: window.innerWidth
                }
            },
            on: {
                init: (swiper) => {
                    debug.log('Swiper initialized');
                    // Store swiper reference for later use
                    this.swiperInstance = swiper;
                    // Update pagination state
                    this._updatePaginationState();
                },
                slideChange: (swiper) => {
                    debug.log('Slide changed, updating pagination');
                    this._updatePaginationState();
                }
            }
        };

        debug.log('Carousel options:', carouselOptions);
        debug.log('Initializing Carousel with container:', this.container);

        // Pass the outer container, Carousel will find .swiper inside
        this.carousel = Carousel(this.container, carouselOptions);

        if (this.carousel) {
            debug.log('Carousel instance created:', this.carousel);
        } else {
            debug.error('Carousel instance is null or undefined');
        }
    }

    /**
     * Setup event listeners for pagination buttons
     */
    _setupPaginationListeners() {
        debug.log('Setting up pagination event listeners');

        // Find all pagination buttons
        this.paginationButtons = this.container.querySelectorAll('[data-show-slide]');
        debug.log(`Found ${this.paginationButtons.length} pagination buttons`);

        // Add click event listeners
        this.paginationButtons.forEach((button) => {
            button.addEventListener('click', (e) => {
                const slideIndex = parseInt(e.currentTarget.getAttribute('data-show-slide'));
                this._goToSlide(slideIndex);
            });
        });
    }

    /**
     * Updates pagination visibility based on active slide
     */
    _updatePaginationState() {
        if (!this.swiperInstance) {
            return;
        }

        const activeIndex = this.swiperInstance.activeIndex;
        debug.log(`Updating pagination visibility, active slide: ${activeIndex}`);

        // Show/hide pagination containers based on active slide
        const paginationContainers = this.container.querySelectorAll('.custom-pagination');
        paginationContainers.forEach((container, index) => {
            container.style.opacity = index === activeIndex ? '1' : '0';
        });
    }

    /**
     * Navigate to a specific slide
     * @param {number} slideIndex - The slide index to navigate to
     */
    _goToSlide(slideIndex) {
        if (!this.swiperInstance) {
            debug.warn('Swiper instance not available');
            return;
        }

        debug.log(`Navigating to slide ${slideIndex}`);
        this.swiperInstance.slideTo(slideIndex);
    }

    /**
     * Setup global resize listener
     */
    _setupResizeListener() {
        debug.log('Setting up resize listener');
        document.addEventListener('theme:resize', this._handleResize);
    }

    /**
     * Handle resize event
     */
    _handleResize(event) {
        const { width } = event.detail;
        debug.log('Resize detected, updating swiper', { width });

        if (this.swiperInstance) {
            this.swiperInstance.params.width = width;
            this.swiperInstance.update();
            debug.log('Swiper updated for new viewport width');
        }
    }

    destroy() {
        debug.log('Destroy called');

        // Remove resize listener
        document.removeEventListener('theme:resize', this._handleResize);

        if (this.carousel && typeof this.carousel.destroy === 'function') {
            this.carousel.destroy();
            debug.log('Carousel destroyed');
        }

        // Clean up all references
        this.swiperInstance = null;
        this.swiperContainer = null;
        this.paginationButtons = [];
    }
}

/**
 * Initialize all text content sliders on the page
 */
function initTextContentSliders() {
    debug.log('initTextContentSliders called');
    const sliders = document.querySelectorAll('[data-text-content-slider]');
    debug.log(`Found ${sliders.length} text content sliders`);

    sliders.forEach((slider, index) => {
        debug.log(`Initializing slider ${index + 1}/${sliders.length}`, slider);

        if (!slider._textContentSlider) {
            try {
                slider._textContentSlider = new TextContentSlider(slider);
                debug.log(`Slider ${index + 1} initialized successfully`);
            } catch (error) {
                debug.error(`Error initializing slider ${index + 1}:`, error);
            }
        } else {
            debug.log(`Slider ${index + 1} already initialized, skipping`);
        }
    });
}

// Initialize on DOM ready
debug.log('Script loaded, readyState:', document.readyState);

if (document.readyState === 'loading') {
    debug.log('Document still loading, adding DOMContentLoaded listener');
    document.addEventListener('DOMContentLoaded', () => {
        debug.log('DOMContentLoaded event fired');
        initTextContentSliders();
    });
} else {
    debug.log('Document already loaded, initializing immediately');
    initTextContentSliders();
}

// Re-initialize on Shopify section load events
document.addEventListener('shopify:section:load', (event) => {
    debug.log('shopify:section:load event fired', event.target);
    const slider = event.target.querySelector('[data-text-content-slider]');
    if (slider) {
        debug.log('Found slider in loaded section', slider);
        if (!slider._textContentSlider) {
            try {
                slider._textContentSlider = new TextContentSlider(slider);
                debug.log('Slider initialized on section load');
            } catch (error) {
                debug.error('Error initializing slider on section load:', error);
            }
        } else {
            debug.log('Slider already initialized, skipping');
        }
    } else {
        debug.log('No slider found in loaded section');
    }
});

// Cleanup on section unload
document.addEventListener('shopify:section:unload', (event) => {
    debug.log('shopify:section:unload event fired', event.target);
    const slider = event.target.querySelector('[data-text-content-slider]');
    if (slider && slider._textContentSlider) {
        debug.log('Destroying slider on section unload');
        slider._textContentSlider.destroy();
        delete slider._textContentSlider;
    }
});

export default TextContentSlider;
